import { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../api';
import { Caricamento, Etichetta, Importo, Scheda, Vuoto, useAvvisi, useCarico } from '../componenti/base';
import { formattaData, plurale } from '../formato';
import { useSessione } from '../main';
import { ImportMovimenti } from './import-movimenti';

interface EsitoFattura {
  numero: string;
  esito: 'CREATO' | 'DUPLICATO' | 'ERRORE';
  tipo?: 'ATTIVA' | 'PASSIVA';
  documentoId?: string;
  controparte?: string;
  totaleCent?: number;
  anagraficaCreata?: boolean;
  messaggio?: string;
}
interface EsitoFile {
  nomeFile: string;
  esito: 'OK' | 'INVARIATO' | 'PARZIALE' | 'ERRORE';
  messaggio?: string;
  fatture: EsitoFattura[];
}
interface Risposta {
  file: EsitoFile[];
  riepilogo: { file: number; creati: number; duplicati: number; fileConErrori: number };
}
interface VoceStorico {
  id: string;
  nomeFile: string;
  esito: string;
  messaggio: string | null;
  documentiCreati: number;
  documentiSaltati: number;
  caricatoIl: string;
}

type Scheda2 = 'FATTURE' | 'MOVIMENTI';

/**
 * L'API accetta un numero limitato di file per richiesta (e un corpo limitato):
 * un anno di fatture passive li supera senza sforzo. I lotti li facciamo qui,
 * così chi carica non deve contarli né sapere che un limite esiste.
 * Un file più grande del lotto parte da solo: sarà l'API a dire se è troppo.
 */
const LOTTO_FILE = 20;
const LOTTO_BYTE = 12 * 1024 * 1024;

function dividiInLotti(files: File[]): File[][] {
  const lotti: File[][] = [];
  let corrente: File[] = [];
  let byte = 0;
  for (const f of files) {
    if (corrente.length > 0 && (corrente.length >= LOTTO_FILE || byte + f.size > LOTTO_BYTE)) {
      lotti.push(corrente);
      corrente = [];
      byte = 0;
    }
    corrente.push(f);
    byte += f.size;
  }
  if (corrente.length > 0) lotti.push(corrente);
  return lotti;
}

export function Importazioni() {
  const { organizzazione } = useSessione();
  const avvisa = useAvvisi();
  const [scheda, setScheda] = useState<Scheda2>('FATTURE');
  const input = useRef<HTMLInputElement>(null);
  const [risposta, setRisposta] = useState<Risposta | null>(null);
  const [invio, setInvio] = useState(false);
  const [progresso, setProgresso] = useState<{ fatti: number; totale: number } | null>(null);
  const [trascinato, setTrascinato] = useState(false);
  const storico = useCarico(() => api.get<VoceStorico[]>('/api/importa/storico'), []);

  const invia = async (files: FileList | File[]) => {
    const lista = Array.from(files);
    if (lista.length === 0) return;
    const lotti = dividiInLotti(lista);
    const esiti: EsitoFile[] = [];
    setInvio(true);
    setProgresso({ fatti: 0, totale: lista.length });
    try {
      for (const lotto of lotti) {
        const form = new FormData();
        for (const f of lotto) form.append('file', f);
        try {
          const r = await api.carica<Risposta>('/api/importa/xml', form);
          esiti.push(...r.file);
        } catch (e) {
          // Un lotto caduto non ferma gli altri: lo registriamo file per file e
          // si prosegue, perché fermarsi a metà farebbe perdere di vista quello
          // che era già entrato.
          const messaggio = e instanceof Error ? e.message : 'Caricamento non riuscito.';
          for (const f of lotto) esiti.push({ nomeFile: f.name, esito: 'ERRORE', messaggio, fatture: [] });
        }
        setProgresso({ fatti: esiti.length, totale: lista.length });
      }
    } finally {
      setInvio(false);
      setProgresso(null);
      if (input.current) input.current.value = '';
    }

    const conta = (esito: EsitoFattura['esito']) =>
      esiti.reduce((t, e) => t + e.fatture.filter((f) => f.esito === esito).length, 0);
    const riepilogo = {
      file: esiti.length,
      creati: conta('CREATO'),
      duplicati: conta('DUPLICATO'),
      fileConErrori: esiti.filter((e) => e.esito === 'ERRORE').length,
    };
    setRisposta({ file: esiti, riepilogo });
    storico.ricarica();
    if (riepilogo.creati > 0) avvisa(`${plurale(riepilogo.creati, 'fattura importata', 'fatture importate')}.`);
    else if (riepilogo.duplicati > 0 && riepilogo.fileConErrori === 0)
      avvisa('Nessuna novità: erano tutte già presenti.', 'attenzione');
    else avvisa('Nessuna fattura importata. Guarda il dettaglio qui sotto.', 'errore');
  };

  return (
    <div className="pagina space-y-5">
      <header>
        <h1 className="text-lg font-semibold tracking-tight">Importazioni</h1>
        <p className="text-sm text-inchiostro-500">
          Da qui entrano i dati: le fatture elettroniche dallo SDI, i movimenti dagli estratti conto.
        </p>
        <div className="mt-3 flex rounded-md border border-inchiostro-300 bg-white p-0.5">
          {([['FATTURE', 'Fatture elettroniche (XML)'], ['MOVIMENTI', 'Movimenti bancari (CBI / CSV / Excel)']] as const).map(
            ([v, t]) => (
              <button
                key={v}
                onClick={() => setScheda(v)}
                className={`rounded px-3 py-1.5 text-sm ${scheda === v ? 'bg-inchiostro-900 text-white' : 'text-inchiostro-600'}`}
              >
                {t}
              </button>
            ),
          )}
        </div>
      </header>

      {scheda === 'MOVIMENTI' ? (
        <ImportMovimenti onImportato={() => storico.ricarica()} />
      ) : (
      <>
      <p className="text-sm text-inchiostro-500">
        File XML in formato FatturaPA, attive e passive insieme: il verso lo riconosce Mastro confrontando la partita
        IVA di {organizzazione.ragioneSociale} con le parti del documento.
      </p>

      <Scheda>
        <div
          onDragOver={(e) => {
            e.preventDefault();
            setTrascinato(true);
          }}
          onDragLeave={() => setTrascinato(false)}
          onDrop={(e) => {
            e.preventDefault();
            setTrascinato(false);
            invia(e.dataTransfer.files);
          }}
          className={`rounded-lg border-2 border-dashed p-10 text-center transition-colors ${
            trascinato ? 'border-saldo bg-saldo-chiaro' : 'border-inchiostro-300'
          }`}
        >
          <p className="text-sm font-medium">Trascina qui le fatture — .xml, .zip o .p7m — oppure</p>
          <button className="bottone-primario mt-3" onClick={() => input.current?.click()} disabled={invio}>
            {invio ? (progresso ? `Caricamento… ${progresso.fatti}/${progresso.totale}` : 'Caricamento…') : 'Scegli i file'}
          </button>
          <input
            ref={input}
            type="file"
            // Anche .zip e .p7m: è così che molte passive arrivano dallo SdI. Il
            // formato vero lo decide il server guardando i byte, non l'estensione.
            accept=".xml,.zip,.p7m,text/xml,application/xml,application/zip"
            multiple
            hidden
            onChange={(e) => e.target.files && invia(e.target.files)}
          />
          <p className="mx-auto mt-4 max-w-lg text-xs text-inchiostro-500">
            Puoi selezionarli tutti insieme, anche centinaia: partono a piccoli lotti, uno dopo l’altro. Un file può
            contenere più fatture (lotto): vengono lette tutte. I file firmati <code>.p7m</code> vanno prima
            estratti dalla busta CAdES. Le fatture già presenti — stesso numero e stessa controparte — non vengono
            inserite una seconda volta.
          </p>
        </div>
      </Scheda>

      {risposta && (
        <Scheda
          titolo="Esito dell’ultimo caricamento"
          sottotitolo={`${plurale(risposta.riepilogo.file, 'file', 'file')} · ${risposta.riepilogo.creati} importate · ${risposta.riepilogo.duplicati} già presenti · ${risposta.riepilogo.fileConErrori} con errori`}
          senzaPadding
        >
          <ul className="divide-y divide-inchiostro-200">
            {risposta.file.map((f, i) => (
              <li key={i} className="p-4">
                <div className="flex items-center gap-2">
                  <Etichetta
                    testo={
                      f.esito === 'OK'
                        ? 'importato'
                        : f.esito === 'INVARIATO'
                          ? 'già presente'
                          : f.esito === 'PARZIALE'
                            ? 'parziale'
                            : 'errore'
                    }
                    tono={
                      f.esito === 'OK'
                        ? 'ok'
                        : f.esito === 'INVARIATO'
                          ? 'neutro'
                          : f.esito === 'PARZIALE'
                            ? 'attenzione'
                            : 'allerta'
                    }
                  />
                  <span className="font-mono text-sm">{f.nomeFile}</span>
                </div>
                {f.messaggio && f.fatture.length === 0 && (
                  <p className="mt-2 text-sm text-allerta">{f.messaggio}</p>
                )}
                {f.fatture.length > 0 && (
                  <ul className="mt-2 space-y-1.5 text-sm">
                    {f.fatture.map((x, j) => (
                      <li key={j} className="flex flex-wrap items-center gap-2">
                        <Etichetta
                          testo={x.esito === 'CREATO' ? 'importata' : x.esito === 'DUPLICATO' ? 'già presente' : 'errore'}
                          tono={x.esito === 'CREATO' ? 'ok' : x.esito === 'DUPLICATO' ? 'neutro' : 'allerta'}
                        />
                        {x.documentoId ? (
                          <Link to={`/fatture/${x.documentoId}`} className="font-medium underline underline-offset-2">
                            {x.numero}
                          </Link>
                        ) : (
                          <span className="font-medium">{x.numero}</span>
                        )}
                        {x.tipo && <span className="text-xs text-inchiostro-500">{x.tipo === 'ATTIVA' ? 'attiva' : 'passiva'}</span>}
                        {x.controparte && <span className="text-inchiostro-600">· {x.controparte}</span>}
                        {x.totaleCent !== undefined && <Importo cent={x.totaleCent} />}
                        {x.anagraficaCreata && <Etichetta testo="nuova anagrafica" tono="info" />}
                        {x.messaggio && <span className="w-full text-xs text-inchiostro-500">{x.messaggio}</span>}
                      </li>
                    ))}
                  </ul>
                )}
              </li>
            ))}
          </ul>
        </Scheda>
      )}

      </>
      )}

      <Scheda titolo="Caricamenti precedenti" senzaPadding>
        {storico.caricamento ? (
          <Caricamento />
        ) : (storico.dati ?? []).length === 0 ? (
          <Vuoto titolo="Nessun file caricato finora" />
        ) : (
          <table className="dati">
            <thead>
              <tr>
                <th>Quando</th>
                <th>File</th>
                <th>Esito</th>
                <th className="text-right">Importate</th>
                <th className="text-right">Saltate</th>
              </tr>
            </thead>
            <tbody>
              {storico.dati!.map((v) => (
                <tr key={v.id}>
                  <td className="num whitespace-nowrap">
                    {formattaData(v.caricatoIl.slice(0, 10), organizzazione.formatoData)}{' '}
                    <span className="text-inchiostro-400">{v.caricatoIl.slice(11, 16)}</span>
                  </td>
                  <td className="font-mono text-xs">{v.nomeFile}</td>
                  <td>
                    <Etichetta
                      testo={v.esito === 'INVARIATO' ? 'già presente' : v.esito.toLowerCase()}
                      tono={
                        v.esito === 'OK'
                          ? 'ok'
                          : v.esito === 'INVARIATO'
                            ? 'neutro'
                            : v.esito === 'PARZIALE'
                              ? 'attenzione'
                              : 'allerta'
                      }
                    />
                    {v.messaggio && <span className="ml-2 text-xs text-inchiostro-500">{v.messaggio}</span>}
                  </td>
                  <td className="num text-right">{v.documentiCreati}</td>
                  <td className="num text-right">{v.documentiSaltati}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Scheda>
    </div>
  );
}
